import type {
  InvoicePayer,
  Invoice as RxInvoice,
} from "$global-providers/rxdb-provider/collections/invoice";
import { Invoice } from "$routes/_lib/billing-v2/invoice";
import type {
  CalculatedInvoice,
  InvoiceLinePricing,
  InvoiceLineSource,
  InvoicePayerReference,
  InvoicePayerSource,
} from "$routes/_lib/billing-v2/invoice-source";
import type { PriceListItemRow } from "$routes/patient/[:patientId]/_lib/invoices/components/InvoiceCard/types";
import {
  isPrivateInsurerName,
  tariffUnitPriceInPence,
} from "$routes/patient/[:patientId]/_lib/invoices-v2/invoice-card/_lib/invoice-view";
import {
  LineItemSupplier,
  PatientType,
  PayerType,
  type SyncableInpatientDetails,
} from "elephant-web/gql";
import * as Yup from "yup";

export const DRAFT_FIELD_NAMES = {
  encounterType: "encounterType",
  ward: "ward",
  patientNumber: "patientNumber",
  admissionDate: "admissionDate",
  dischargeDate: "dischargeDate",
  items: "items",
  memberships: "memberships",
  insuranceNames: "insuranceNames",
} as const;

const WARD_REQUIRED_MESSAGE = "Choose a ward";
const PATIENT_NUMBER_REQUIRED_MESSAGE = "Enter a patient number";
const QUANTITY_MESSAGE = "Enter a whole number greater than zero";
const ITEM_REQUIRED_MESSAGE = "Choose an item";
const DEPARTMENT_REQUIRED_MESSAGE = "Choose a revenue department";
const SUPPLIER_REQUIRED_MESSAGE = "Choose a supplier";
const PAYER_REQUIRED_MESSAGE = "Choose a payer";
const MEMBERSHIP_REQUIRED_MESSAGE = "Enter a membership number";
const INSURANCE_NAME_REQUIRED_MESSAGE = "Enter the insurance name";

export const draftAutosaveSchema = Yup.object({
  encounterType: Yup.string().required(),
  ward: Yup.string().when("encounterType", {
    is: PatientType.Inpatient,
    then: (schema) => schema.trim().required(WARD_REQUIRED_MESSAGE),
    otherwise: (schema) => schema.notRequired(),
  }),
  patientNumber: Yup.string().when("encounterType", {
    is: PatientType.Inpatient,
    then: (schema) => schema.trim().required(PATIENT_NUMBER_REQUIRED_MESSAGE),
    otherwise: (schema) => schema.notRequired(),
  }),
  admissionDate: Yup.string(),
  dischargeDate: Yup.string(),
  items: Yup.array(
    Yup.object({
      id: Yup.string().required(),
      name: Yup.string().required(ITEM_REQUIRED_MESSAGE),
      department: Yup.string().required(DEPARTMENT_REQUIRED_MESSAGE),
      supplier: Yup.string().required(SUPPLIER_REQUIRED_MESSAGE),
      quantity: Yup.string()
        .required(QUANTITY_MESSAGE)
        .matches(/^\d+$/, QUANTITY_MESSAGE)
        .test("positive", QUANTITY_MESSAGE, (value) => Number(value) > 0),
      payer: Yup.string().required(PAYER_REQUIRED_MESSAGE),
    }),
  ).required(),
  memberships: Yup.object(),
  insuranceNames: Yup.object(),
});

export type DraftInvoiceInsurer = {
  tariffId: string;
  name: string;
  lineItemIds: string[];
};

export const draftFinaliseSchema = (insurers: readonly DraftInvoiceInsurer[]) =>
  draftAutosaveSchema.shape({
    memberships: Yup.object(
      Object.fromEntries(
        insurers.map((insurer) => [
          insurer.tariffId,
          Yup.string().trim().required(MEMBERSHIP_REQUIRED_MESSAGE),
        ]),
      ),
    ),
    insuranceNames: Yup.object(
      Object.fromEntries(
        insurers
          .filter((insurer) => isPrivateInsurerName(insurer.name))
          .map((insurer) => [
            insurer.tariffId,
            Yup.string().trim().required(INSURANCE_NAME_REQUIRED_MESSAGE),
          ]),
      ),
    ),
  });

export type DraftInvoiceItemValue = {
  id: string;
  name: string;
  department: string;
  supplier: string;
  quantity: string;
  payer: string;
};

export type DraftInvoiceFormValues = {
  encounterType: string;
  ward: string;
  patientNumber: string;
  admissionDate: string;
  dischargeDate: string;
  items: DraftInvoiceItemValue[];
  memberships: Record<string, string>;
  insuranceNames: Record<string, string>;
};

const DRAFT_ITEM_FIELDS: readonly (keyof DraftInvoiceItemValue)[] = [
  "name",
  "department",
  "supplier",
  "quantity",
  "payer",
];

const advanceRecordBaseline = (
  current: Record<string, string>,
  attempted: Record<string, string>,
  saved: Record<string, string>,
): Record<string, string> => {
  const result = { ...current };
  for (const key of new Set([
    ...Object.keys(attempted),
    ...Object.keys(saved),
  ])) {
    if ((attempted[key] ?? "") !== (saved[key] ?? "")) {
      result[key] = saved[key] ?? "";
    }
  }
  return result;
};

export const advanceDraftFormBaseline = ({
  current,
  attempted,
  saved,
}: {
  current: DraftInvoiceFormValues;
  attempted: DraftInvoiceFormValues;
  saved: DraftInvoiceFormValues;
}): DraftInvoiceFormValues => {
  const attemptedById = new Map(attempted.items.map((item) => [item.id, item]));
  const savedById = new Map(saved.items.map((item) => [item.id, item]));
  const items = current.items
    .filter((item) => !(attemptedById.has(item.id) && !savedById.has(item.id)))
    .map((item) => {
      const attemptedItem = attemptedById.get(item.id);
      const savedItem = savedById.get(item.id);
      if (!attemptedItem || !savedItem) return item;
      return DRAFT_ITEM_FIELDS.reduce(
        (result, field) =>
          attemptedItem[field] === savedItem[field]
            ? result
            : { ...result, [field]: savedItem[field] },
        { ...item },
      );
    });
  for (const item of saved.items) {
    if (
      !attemptedById.has(item.id) &&
      !items.some(({ id }) => id === item.id)
    ) {
      items.push({ ...item });
    }
  }
  return {
    encounterType:
      attempted.encounterType === saved.encounterType
        ? current.encounterType
        : saved.encounterType,
    ward: attempted.ward === saved.ward ? current.ward : saved.ward,
    patientNumber:
      attempted.patientNumber === saved.patientNumber
        ? current.patientNumber
        : saved.patientNumber,
    admissionDate:
      attempted.admissionDate === saved.admissionDate
        ? current.admissionDate
        : saved.admissionDate,
    dischargeDate:
      attempted.dischargeDate === saved.dischargeDate
        ? current.dischargeDate
        : saved.dischargeDate,
    items,
    memberships: advanceRecordBaseline(
      current.memberships,
      attempted.memberships,
      saved.memberships,
    ),
    insuranceNames: advanceRecordBaseline(
      current.insuranceNames,
      attempted.insuranceNames,
      saved.insuranceNames,
    ),
  };
};

const isSupplier = (value: string): value is LineItemSupplier =>
  (Object.values(LineItemSupplier) as string[]).includes(value);

export const isInpatient = (encounterType: string): boolean =>
  encounterType === PatientType.Inpatient;

const toDateInputValue = (value: string | null | undefined): string =>
  value ? value.slice(0, 10) : "";

const toStoredDate = (value: string): string | null =>
  value ? new Date(value).toISOString() : null;

export const toDraftFormValues = (
  invoice: Pick<RxInvoice, "lineItems" | "patientType" | "inpatientDetails">,
  membershipByTariffId: Record<string, string>,
  insuranceNameByTariffId: Record<string, string>,
): DraftInvoiceFormValues => ({
  encounterType: invoice.patientType,
  ward: invoice.inpatientDetails?.ward ?? "",
  patientNumber: invoice.inpatientDetails?.inpatientNumber ?? "",
  admissionDate: toDateInputValue(invoice.inpatientDetails?.admission),
  dischargeDate: toDateInputValue(invoice.inpatientDetails?.discharge),
  items: invoice.lineItems
    .filter((lineItem) => !lineItem.disabled)
    .map((lineItem) => ({
      id: lineItem.id,
      name: lineItem.priceListItemId ?? "",
      department: lineItem.revenueDepartmentId ?? "",
      supplier: lineItem.supplier ?? "",
      quantity: lineItem.quantity == null ? "" : String(lineItem.quantity),
      payer: lineItem.tariffId ?? "",
    })),
  memberships: membershipByTariffId,
  insuranceNames: insuranceNameByTariffId,
});

export const draftInsurers = (
  items: readonly DraftInvoiceItemValue[],
  itemsById: ReadonlyMap<string, PriceListItemRow>,
): DraftInvoiceInsurer[] => {
  const insurers = new Map<string, DraftInvoiceInsurer>();
  for (const item of items) {
    const tariff = itemsById
      .get(item.name)
      ?.payers?.find((candidate) => candidate.tariffId === item.payer);
    if (!tariff?.tariffId || !tariff.name || tariff.name === "Patient")
      continue;
    const existing = insurers.get(tariff.tariffId);
    insurers.set(
      tariff.tariffId,
      existing
        ? { ...existing, lineItemIds: [...existing.lineItemIds, item.id] }
        : {
            tariffId: tariff.tariffId,
            name: tariff.name,
            lineItemIds: [item.id],
          },
    );
  }
  return [...insurers.values()];
};

const matchPayersToInsurers = (
  payers: readonly InvoicePayer[],
  insurers: readonly DraftInvoiceInsurer[],
): Map<string, InvoicePayer> => {
  const unmatched = payers.filter((payer) => payer.type === PayerType.Insurer);
  const result = new Map<string, InvoicePayer>();

  for (const insurer of insurers) {
    const lineItemIds = new Set(insurer.lineItemIds);
    const claimedLineItemMatch = unmatched.findIndex((payer) =>
      payer.claimedLineItems.some((item) => lineItemIds.has(item.lineItemId)),
    );
    const nameMatch = unmatched.findIndex(
      (payer) => payer.name === insurer.name,
    );
    const matchIndex =
      claimedLineItemMatch === -1 ? nameMatch : claimedLineItemMatch;

    if (matchIndex !== -1) {
      const [payer] = unmatched.splice(matchIndex, 1);
      result.set(insurer.tariffId, payer);
    }
  }

  return result;
};

const insurerDetailsOnInvoice = (
  payers: readonly InvoicePayer[],
  insurers: readonly DraftInvoiceInsurer[],
  read: (
    details: InvoicePayer["insuranceDetails"],
  ) => string | null | undefined,
): Record<string, string> => {
  const payerByTariffId = matchPayersToInsurers(payers, insurers);
  return Object.fromEntries(
    insurers
      .map(
        (insurer) =>
          [
            insurer.tariffId,
            read(
              payerByTariffId.get(insurer.tariffId)?.insuranceDetails ?? null,
            ) ?? "",
          ] as const,
      )
      .filter(([, detail]) => detail !== ""),
  );
};

export const membershipsOnInvoice = (
  payers: readonly InvoicePayer[],
  insurers: readonly DraftInvoiceInsurer[],
): Record<string, string> =>
  insurerDetailsOnInvoice(
    payers,
    insurers,
    (details) => details?.insuranceNumber,
  );

export const insuranceNamesOnInvoice = (
  payers: readonly InvoicePayer[],
  insurers: readonly DraftInvoiceInsurer[],
): Record<string, string> =>
  insurerDetailsOnInvoice(
    payers,
    insurers.filter((insurer) => isPrivateInsurerName(insurer.name)),
    (details) => details?.name,
  );

export const emptyDraftItem = (id: string): DraftInvoiceItemValue => ({
  id,
  name: "",
  department: "",
  supplier: LineItemSupplier.Government,
  quantity: "1",
  payer: "",
});

const buildInpatientDetails = (
  baseline: DraftInvoiceFormValues,
  values: DraftInvoiceFormValues,
  existing: SyncableInpatientDetails | null,
): SyncableInpatientDetails | null => {
  if (!isInpatient(values.encounterType)) return null;
  const encounterTypeChanged = baseline.encounterType !== values.encounterType;
  return {
    ...existing,
    ward:
      encounterTypeChanged || baseline.ward !== values.ward
        ? values.ward || null
        : (existing?.ward ?? null),
    inpatientNumber:
      encounterTypeChanged || baseline.patientNumber !== values.patientNumber
        ? values.patientNumber || null
        : (existing?.inpatientNumber ?? null),
    admission:
      encounterTypeChanged || baseline.admissionDate !== values.admissionDate
        ? toStoredDate(values.admissionDate)
        : (existing?.admission ?? null),
    discharge:
      encounterTypeChanged || baseline.dischargeDate !== values.dischargeDate
        ? toStoredDate(values.dischargeDate)
        : (existing?.discharge ?? null),
    dailyRebateAmount: existing?.dailyRebateAmount ?? null,
  };
};

const draftItemPricing = (
  item: DraftInvoiceItemValue,
  itemsById: ReadonlyMap<string, PriceListItemRow>,
  existingPayers: readonly InvoicePayerSource[],
): InvoiceLinePricing => {
  const catalogueItem = itemsById.get(item.name);
  const tariff = catalogueItem?.payers?.find(
    (candidate) => candidate.tariffId === item.payer,
  );
  const cost = tariffUnitPriceInPence(catalogueItem, item.payer);
  if (!catalogueItem || !tariff?.name || cost === null) {
    throw new Error("Draft line item requires a valid catalogue tariff");
  }
  const insurer = existingPayers.find(
    (payer) => payer.type === PayerType.Insurer && payer.name === tariff.name,
  );
  const payer: InvoicePayerReference =
    tariff.name === "Patient"
      ? { type: PayerType.Patient }
      : {
          type: PayerType.Insurer,
          name: tariff.name,
          ...(insurer?.id ? { id: insurer.id } : {}),
        };
  return {
    cost,
    tariffId: item.payer,
    priceListItemId: item.name,
    revenueDepartmentId: item.department,
    payer,
  };
};

const recordChanged = (
  current: Record<string, string>,
  baseline: Record<string, string>,
  key: string,
): boolean => (current[key] ?? "") !== (baseline[key] ?? "");

const itemChanged = <K extends keyof DraftInvoiceItemValue>(
  current: DraftInvoiceItemValue,
  baseline: DraftInvoiceItemValue,
  keys: readonly K[],
): boolean => keys.some((key) => current[key] !== baseline[key]);

const insurerRoster = (
  invoice: Invoice,
  baseline: DraftInvoiceFormValues,
  values: DraftInvoiceFormValues,
  itemsById: ReadonlyMap<string, PriceListItemRow>,
  payerIdForTariffId: (tariffId: string) => string,
): InvoicePayerSource[] => {
  const insurers = draftInsurers(values.items, itemsById);
  const current = invoice
    .toSource()
    .payers.filter((payer) => payer.type === PayerType.Insurer);
  const roster = new Map(current.map((payer) => [payer.id, payer]));
  const payerByTariffId = matchPayersToInsurers(
    invoice.toRecord().payers,
    insurers,
  );
  for (const insurer of insurers) {
    const existing = payerByTariffId.get(insurer.tariffId);
    const id = existing?.id ?? payerIdForTariffId(insurer.tariffId);
    const details = existing?.insuranceDetails ?? null;
    roster.set(id, {
      ...roster.get(id),
      id,
      name: insurer.name,
      type: PayerType.Insurer,
      insuranceDetails: {
        ...details,
        name: recordChanged(
          values.insuranceNames,
          baseline.insuranceNames,
          insurer.tariffId,
        )
          ? values.insuranceNames[insurer.tariffId] || null
          : (details?.name ?? null),
        insuranceNumber: recordChanged(
          values.memberships,
          baseline.memberships,
          insurer.tariffId,
        )
          ? values.memberships[insurer.tariffId] || null
          : (details?.insuranceNumber ?? null),
        dependent: details?.dependent ?? null,
      },
    });
  }
  return [...roster.values()];
};

const addDraftLine = (
  invoice: Invoice,
  item: DraftInvoiceItemValue,
  itemsById: ReadonlyMap<string, PriceListItemRow>,
): Invoice => {
  const pricing = draftItemPricing(item, itemsById, invoice.toSource().payers);
  const line: InvoiceLineSource = {
    id: item.id,
    quantity: Number(item.quantity),
    disabled: false,
    automated: false,
    supplier: isSupplier(item.supplier) ? item.supplier : null,
    missingPrice: false,
    ...pricing,
  };
  return invoice.addLineItem(line);
};

const isCompleteDraftItem = (item: DraftInvoiceItemValue): boolean =>
  Boolean(
    item.name &&
      item.department &&
      item.supplier &&
      item.payer &&
      /^\d+$/.test(item.quantity) &&
      Number(item.quantity) > 0,
  );

export const applyDraftInvoiceValues = ({
  record,
  baseline,
  values,
  itemsById,
  payerIdForTariffId,
  updatedAt,
  updatedByUserId,
}: {
  record: RxInvoice;
  baseline: DraftInvoiceFormValues;
  values: DraftInvoiceFormValues;
  itemsById: ReadonlyMap<string, PriceListItemRow>;
  payerIdForTariffId: (tariffId: string) => string;
  updatedAt?: string;
  updatedByUserId?: string;
}): CalculatedInvoice => {
  let invoice = Invoice.fromRecord(record);
  invoice = invoice.updateInsurance(
    insurerRoster(invoice, baseline, values, itemsById, payerIdForTariffId),
  );

  const baselineById = new Map(baseline.items.map((item) => [item.id, item]));
  const valuesById = new Map(values.items.map((item) => [item.id, item]));

  for (const item of baseline.items) {
    if (
      !valuesById.has(item.id) &&
      invoice.toSource().lineItems.some((line) => line.id === item.id)
    ) {
      invoice = invoice.removeLineItem(item.id);
    }
  }

  for (const item of values.items) {
    const previous = baselineById.get(item.id);
    const latest = invoice
      .toSource()
      .lineItems.find((line) => line.id === item.id);
    if (!previous) {
      if (!latest && isCompleteDraftItem(item)) {
        invoice = addDraftLine(invoice, item, itemsById);
      }
      continue;
    }
    if (!latest) continue;
    if (itemChanged(item, previous, ["name", "department", "payer"])) {
      invoice = invoice.updateLinePricing(
        item.id,
        draftItemPricing(item, itemsById, invoice.toSource().payers),
      );
    }
    if (itemChanged(item, previous, ["quantity", "supplier"])) {
      invoice = invoice.updateLineItem(item.id, {
        ...(item.quantity !== previous.quantity
          ? { quantity: Number(item.quantity) }
          : {}),
        ...(item.supplier !== previous.supplier
          ? {
              supplier: isSupplier(item.supplier) ? item.supplier : null,
            }
          : {}),
      });
    }
  }

  const usedInsurerIds = new Set(
    invoice
      .toSource()
      .lineItems.flatMap((line) =>
        line.payer.type === PayerType.Insurer && line.payer.id
          ? [line.payer.id]
          : [],
      ),
  );
  const usedInsurerNames = new Set(
    invoice
      .toSource()
      .lineItems.flatMap((line) =>
        line.payer.type === PayerType.Insurer && !line.payer.id
          ? [line.payer.name]
          : [],
      ),
  );
  invoice = invoice.updateInsurance(
    invoice
      .toSource()
      .payers.filter(
        (payer) =>
          payer.type === PayerType.Insurer &&
          (usedInsurerIds.has(payer.id) || usedInsurerNames.has(payer.name)),
      ),
  );

  if (
    baseline.encounterType !== values.encounterType ||
    baseline.ward !== values.ward ||
    baseline.patientNumber !== values.patientNumber ||
    baseline.admissionDate !== values.admissionDate ||
    baseline.dischargeDate !== values.dischargeDate
  ) {
    invoice = invoice.updateEncounter({
      patientType: isInpatient(values.encounterType)
        ? PatientType.Inpatient
        : PatientType.Outpatient,
      inpatientDetails: buildInpatientDetails(
        baseline,
        values,
        invoice.toSource().inpatientDetails,
      ),
    });
  }

  const source = invoice.toSource();
  return Invoice.from({
    ...source,
    meta: {
      ...source.meta,
      ...(updatedAt ? { updatedAt } : {}),
      ...(updatedByUserId ? { updatedByUserId } : {}),
    },
  }).toRecord();
};
